import React, { useState, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import '@/App.css';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ExternalLink, TrendingDown, Package, Star, Trophy } from 'lucide-react';

// ============================================
// SINGLE SOURCE OF TRUTH - PRODUCT DATA
// ============================================
const PRODUCTS = {
  myprotein: {
    id: 'myprotein',
    name: 'MyProtein Creatine Gummies',
    packPrice: 22.99,
    packSize: 60,
    dailyGummies: 5,
    sugarPerGummy: 3.0,
    // Calculated values
    get dailySugar() {
      return this.dailyGummies * this.sugarPerGummy;
    },
    get daysPerPack() {
      return this.packSize / this.dailyGummies;
    },
    get packsPerMonth() {
      return 30 / this.daysPerPack;
    },
    get monthlyCost() {
      return this.packsPerMonth * this.packPrice;
    },
    get annualCost() {
      return this.monthlyCost * 12;
    },
    get annualSugar() {
      return this.dailySugar * 365;
    },
    get description() {
      return `${this.dailyGummies} gummies/day for ~5g creatine`;
    }
  },
  appliednutrition: {
    id: 'appliednutrition',
    name: 'Applied Nutrition Creatine Gummies',
    packPrice: 19.99,
    packSize: 60,
    dailyGummies: 7,
    sugarPerGummy: 2.5,
    // Calculated values
    get dailySugar() {
      return this.dailyGummies * this.sugarPerGummy;
    },
    get daysPerPack() {
      return this.packSize / this.dailyGummies;
    },
    get packsPerMonth() {
      return 30 / this.daysPerPack;
    },
    get monthlyCost() {
      return this.packsPerMonth * this.packPrice;
    },
    get annualCost() {
      return this.monthlyCost * 12;
    },
    get annualSugar() {
      return this.dailySugar * 365;
    },
    get description() {
      return `${this.dailyGummies} gummies/day for ~5g creatine`;
    }
  },
  generic: {
    id: 'generic',
    name: 'Other Brand / Generic Average',
    packPrice: 24.99,
    packSize: 60,
    dailyGummies: 5,
    sugarPerGummy: 3.0,
    // Calculated values
    get dailySugar() {
      return this.dailyGummies * this.sugarPerGummy;
    },
    get daysPerPack() {
      return this.packSize / this.dailyGummies;
    },
    get packsPerMonth() {
      return 30 / this.daysPerPack;
    },
    get monthlyCost() {
      return this.packsPerMonth * this.packPrice;
    },
    get annualCost() {
      return this.monthlyCost * 12;
    },
    get annualSugar() {
      return this.dailySugar * 365;
    },
    get description() {
      return '5 gummies/day for ~5g creatine';
    }
  },
  powder: {
    id: 'powder',
    name: 'Powder Supplement',
    description: 'Pure creatine monohydrate',
    monthlyCost: 5.77,
    get annualCost() {
      return this.monthlyCost * 12;
    },
    dailySugar: 0,
    get annualSugar() {
      return 0;
    }
  }
};

// Marketplace deals
const MARKETPLACE_DEALS = [
  {
    id: 'amazon',
    icon: Trophy,
    title: '🏆 Best Market Price',
    subtitle: 'Amazon',
    price: 9.99,
    description: 'Nutrition Geeks 315g',
    link: 'https://amzn.to/4biXznE'
  },
  {
    id: 'myprotein',
    icon: Star,
    title: '⭐ Premium Choice',
    subtitle: 'MyProtein',
    price: 3.99,
    description: '100g Trial Pouch',
    link: 'https://www.myprotein.com/nutrition/creatine-monohydrate-powder/10530050.html'
  },
  {
    id: 'bulk',
    icon: Package,
    title: '📦 Bulk Saver',
    subtitle: '1kg Pack',
    price: 29.99,
    description: '6 Month Supply',
    link: 'https://www.bulk.com/uk/creatine-monohydrate.html'
  }
];

// Utility function to safely format numbers
const safeFormat = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value) || !isFinite(value)) {
    return 'Update Pending';
  }
  return value.toFixed(decimals);
};

const safeFormatCurrency = (value) => {
  if (value === null || value === undefined || isNaN(value) || !isFinite(value)) {
    return 'Update Pending';
  }
  return `£${value.toFixed(2)}`;
};

function HomePage() {
  const [selectedBrand, setSelectedBrand] = useState('myprotein');
  const [viewMode, setViewMode] = useState('annual'); // 'monthly' or 'annual'

  const selectedGummy = useMemo(() => PRODUCTS[selectedBrand], [selectedBrand]);
  const powder = PRODUCTS.powder;

  const gummyCost = viewMode === 'annual' ? selectedGummy.annualCost : selectedGummy.monthlyCost;
  const powderCost = viewMode === 'annual' ? powder.annualCost : powder.monthlyCost;
  const savings = gummyCost - powderCost;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center space-x-2">
              <div className="text-2xl font-bold text-green-600">🍬</div>
              <h1 className="text-2xl font-bold text-gray-900">Gummy Truth UK</h1>
              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">v2.2</span>
            </Link>
            <nav className="flex items-center space-x-6">
              <Link 
                to="/" 
                className="text-sm font-medium text-gray-700 hover:text-gray-900 transition"
              >
                Home
              </Link>
              <Link 
                to="/blog" 
                className="text-sm font-medium text-gray-700 hover:text-gray-900 transition"
              >
                Blog
              </Link>
              <Link 
                to="/guide" 
                className="text-sm font-medium text-gray-700 hover:text-gray-900 transition"
                data-testid="nav-guide-link"
              >
                Guide
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main id="calculator" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Title Section */}
        <div className="text-center mb-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Gummy vs Powder Comparison
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Compare real UK brands or enter custom data to see annual costs and sugar load
          </p>
        </div>

        {/* Brand Selector */}
        <div className="max-w-2xl mx-auto mb-8">
          <Card>
            <CardContent className="pt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Your Brand
              </label>
              <Select value={selectedBrand} onValueChange={setSelectedBrand}>
                <SelectTrigger data-testid="brand-selector" className="w-full">
                  <SelectValue placeholder="Select a brand" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="myprotein" data-testid="brand-option-myprotein">
                    MyProtein Creatine Gummies
                  </SelectItem>
                  <SelectItem value="appliednutrition" data-testid="brand-option-appliednutrition">
                    Applied Nutrition Creatine Gummies
                  </SelectItem>
                  <SelectItem value="generic" data-testid="brand-option-generic">
                    Other Brand / Generic Average
                  </SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        </div>

        {/* View Mode Toggle */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
            <button
              onClick={() => setViewMode('monthly')}
              data-testid="view-mode-monthly"
              className={`px-6 py-2 rounded-md text-sm font-medium transition ${
                viewMode === 'monthly'
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              📅 Monthly
            </button>
            <button
              onClick={() => setViewMode('annual')}
              data-testid="view-mode-annual"
              className={`px-6 py-2 rounded-md text-sm font-medium transition ${
                viewMode === 'annual'
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              📊 Annual
            </button>
          </div>
        </div>

        {/* Comparison Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {/* Gummy Card */}
          <Card className="border-2 border-orange-200 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-orange-50 to-red-50">
              <CardTitle className="text-2xl" data-testid="gummy-card-title">
                {selectedGummy.name}
              </CardTitle>
              <CardDescription data-testid="gummy-card-description">
                {selectedGummy.description}
              </CardDescription>
              {/* Dosage Reality Badge */}
              <div className="mt-2">
                {selectedBrand === 'myprotein' && (
                  <div className="bg-orange-100 border border-orange-300 rounded-md px-3 py-2">
                    <p className="text-xs font-semibold text-orange-800">
                      ⚠️ Requires 5 gummies for full 5g dose
                    </p>
                  </div>
                )}
                {selectedBrand === 'appliednutrition' && (
                  <div className="bg-red-100 border border-red-300 rounded-md px-3 py-2">
                    <p className="text-xs font-semibold text-red-800">
                      ⚠️ Label dose is only 3g. You need ~7 gummies for 5g!
                    </p>
                  </div>
                )}
                {selectedBrand === 'generic' && (
                  <div className="bg-orange-100 border border-orange-300 rounded-md px-3 py-2">
                    <p className="text-xs font-semibold text-orange-800">
                      ⚠️ Requires 5 gummies for full 5g dose
                    </p>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              {/* Cost - Largest Display */}
              <div className="text-center">
                <p className="text-sm text-gray-500 mb-2">
                  {viewMode === 'annual' ? 'Annual' : 'Monthly'} Cost:
                </p>
                <p className="text-5xl font-bold text-red-600" data-testid="gummy-cost">
                  {safeFormatCurrency(gummyCost)}
                </p>
              </div>

              <div className="border-t pt-4 space-y-3">
                {/* Daily Gummies */}
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Daily Gummies:</span>
                  <span className="font-semibold text-lg" data-testid="gummy-daily-dose">
                    {safeFormat(selectedGummy.dailyGummies, 0)} gummies
                  </span>
                </div>

                {/* Sugar per Day */}
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Sugar per Day:</span>
                  <span className="font-semibold text-lg text-orange-600" data-testid="gummy-daily-sugar">
                    {safeFormat(selectedGummy.dailySugar, 1)}g
                  </span>
                </div>

                {/* Annual Sugar Load */}
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700 mb-1">Annual Sugar Load:</p>
                  <p className="text-3xl font-bold text-red-600" data-testid="gummy-annual-sugar">
                    {safeFormat(selectedGummy.annualSugar / 1000, 2)} kg
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    = {safeFormat(selectedGummy.annualSugar, 0)} g
                  </p>
                </div>
              </div>

              <Button 
                variant="outline" 
                className="w-full" 
                asChild
                data-testid="check-gummy-price-button"
              >
                <a 
                  href={
                    selectedBrand === 'myprotein' 
                      ? 'https://www.myprotein.com/p/sports-nutrition/creatine-gummies/13528283/?variation=16747362'
                      : selectedBrand === 'appliednutrition'
                      ? 'https://appliednutrition.uk/products/pure-creatine-gummies'
                      : 'https://www.myprotein.com/p/sports-nutrition/creatine-gummies/13528283/?variation=16747362'
                  }
                  target="_blank" 
                  rel="nofollow sponsored noopener noreferrer"
                >
                  Check {selectedBrand === 'myprotein' ? 'MyProtein' : selectedBrand === 'appliednutrition' ? 'Applied Nutrition' : 'Product'} Price <ExternalLink className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </CardContent>
          </Card>

          {/* Powder Card */}
          <Card className="border-2 border-green-200 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
              <CardTitle className="text-2xl" data-testid="powder-card-title">
                {powder.name}
              </CardTitle>
              <CardDescription data-testid="powder-card-description">
                {powder.description}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              {/* Cost - Largest Display */}
              <div className="text-center">
                <p className="text-sm text-gray-500 mb-2">
                  {viewMode === 'annual' ? 'Annual' : 'Monthly'} Cost:
                </p>
                <p className="text-5xl font-bold text-green-600" data-testid="powder-cost">
                  {safeFormatCurrency(powderCost)}
                </p>
              </div>

              <div className="border-t pt-4 space-y-3">
                {/* Sugar Info */}
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700 mb-1">Annual Sugar Load:</p>
                  <p className="text-3xl font-bold text-green-600" data-testid="powder-sugar">
                    0 kg
                  </p>
                  <p className="text-sm text-green-700 mt-1 font-medium">Zero sugar</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Savings Banner */}
        <Card className="bg-gradient-to-r from-green-500 to-emerald-600 text-white border-0 shadow-xl mb-12">
          <CardContent className="py-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <TrendingDown className="h-12 w-12" />
                <div>
                  <p className="text-lg font-medium opacity-90">
                    {viewMode === 'annual' ? 'Annual' : 'Monthly'} Savings (Powder vs Gummy):
                  </p>
                  <p className="text-xs opacity-75 mt-1">
                    Switch to powder and save money while cutting sugar
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-6xl font-bold" data-testid="savings-amount">
                  {safeFormatCurrency(savings)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Marketplace Section */}
        <div id="marketplace" className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            💰 Best Powder Deals on the Market
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {MARKETPLACE_DEALS.map((deal, index) => (
              <Card 
                key={deal.id} 
                className="border-2 hover:border-green-400 transition shadow-lg hover:shadow-xl"
                data-testid={`marketplace-card-${deal.id}`}
              >
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <deal.icon className="h-6 w-6 text-green-600" />
                    <span>{deal.title}</span>
                  </CardTitle>
                  <CardDescription className="text-lg">{deal.subtitle}</CardDescription>
                  {deal.description && (
                    <p className="text-sm text-blue-600 font-medium mt-1">{deal.description}</p>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <p className="text-4xl font-bold text-gray-900">
                      £{deal.price.toFixed(2)}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">One-time purchase</p>
                  </div>
                  <Button 
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold"
                    asChild
                    data-testid={`marketplace-card-button-${deal.id}`}
                  >
                    <a href={deal.link} target="_blank" rel="nofollow sponsored noopener noreferrer">
                      View Deal <ExternalLink className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center space-y-4">
            <p className="text-sm font-medium text-gray-700">
              Calculations based on publicly available nutrition labels.
            </p>
            <p className="text-sm text-gray-900">
              <strong>Gummy Truth UK © 2024 | Marketplace v2.2</strong>
            </p>
            <p className="text-xs text-gray-600 max-w-3xl mx-auto">
              <strong>Disclaimer:</strong> This tool is a comparative cost analyzer for educational purposes only. 
              Brand pricing reflects current market rates. Affiliate links support this research. Not medical advice.
            </p>
            <div className="flex justify-center space-x-6 text-sm">
              <Link to="/privacy" className="text-blue-600 hover:underline" data-testid="footer-privacy-link">
                Privacy Policy
              </Link>
              <Link to="/methodology" className="text-blue-600 hover:underline" data-testid="footer-methodology-link">
                Methodology
              </Link>
              <Link to="/blog" className="text-blue-600 hover:underline" data-testid="footer-blog-link">
                Blog
              </Link>
              <Link to="/guide" className="text-blue-600 hover:underline" data-testid="footer-guide-link">
                Creatine Guide
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function GuidePage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center space-x-2">
              <div className="text-2xl font-bold text-green-600">🍬</div>
              <h1 className="text-2xl font-bold text-gray-900">Gummy Truth UK</h1>
            </Link>
            <Link 
              to="/" 
              className="text-sm font-medium text-gray-700 hover:text-gray-900"
              data-testid="guide-back-link"
            >
              ← Back to Calculator
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            Why Powder Beats Gummies (The Truth)
          </h1>
          <p className="text-xl text-gray-600">
            The Truth About Creatine Supplements
          </p>
        </div>

        {/* Comparison Table */}
        <Card className="mb-12 shadow-xl">
          <CardHeader className="bg-gradient-to-r from-green-500 to-blue-500 text-white">
            <CardTitle className="text-2xl text-center">The Real Comparison</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="comparison-table">
                <thead>
                  <tr className="border-b-2">
                    <th className="text-left py-4 px-4 text-gray-700">Factor</th>
                    <th className="text-center py-4 px-4 text-green-700 font-bold">💪 Powder</th>
                    <th className="text-center py-4 px-4 text-red-700 font-bold">🍬 Gummies</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-4 px-4 font-medium">Monthly Cost</td>
                    <td className="text-center py-4 px-4 text-green-600 font-bold text-lg">~£5.77</td>
                    <td className="text-center py-4 px-4 text-red-600 font-bold text-lg">£57-70</td>
                  </tr>
                  <tr className="border-b bg-gray-50">
                    <td className="py-4 px-4 font-medium">
                      Sugar Content
                      <p className="text-xs text-gray-500 mt-1 font-normal">
                        Based on label-reported sugar × required daily dose
                      </p>
                    </td>
                    <td className="text-center py-4 px-4 text-green-600 font-bold text-lg">0g</td>
                    <td className="text-center py-4 px-4 text-red-600 font-bold text-lg">15-17.5g/day</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-4 px-4 font-medium">Purity</td>
                    <td className="text-center py-4 px-4 text-green-600 font-bold">✓ 100%</td>
                    <td className="text-center py-4 px-4 text-red-600 font-bold">✗ Additives</td>
                  </tr>
                  <tr className="border-b bg-gray-50">
                    <td className="py-4 px-4 font-medium">Research-Backed</td>
                    <td className="text-center py-4 px-4 text-green-600 font-bold">✓ Yes</td>
                    <td className="text-center py-4 px-4 text-orange-600 font-bold">~ Limited</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4 font-medium">Annual Savings vs Gummies</td>
                    <td className="text-center py-4 px-4 text-green-600 font-bold text-xl">£600+</td>
                    <td className="text-center py-4 px-4 text-gray-400">—</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* How We Calculate Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">📐 How We Calculate</CardTitle>
          </CardHeader>
          <CardContent className="prose max-w-none">
            <p className="text-gray-700 leading-relaxed">
              All calculations are normalized to <strong>5g of creatine per day</strong>, which is the 
              scientifically recommended daily dose for maintenance.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              <strong>Why this matters:</strong>
            </p>
            <ul className="list-disc list-inside space-y-2 mt-2 text-gray-700">
              <li>Different gummy brands have different creatine concentrations per gummy</li>
              <li>To achieve the same 5g daily dose, you need different quantities of gummies</li>
              <li>More gummies = more sugar, more cost per day</li>
              <li>Our calculator shows the <em>real</em> cost and sugar load of achieving the proper dosage</li>
            </ul>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mt-6">
              <p className="font-bold text-blue-800">
                Example: If a gummy contains 1g creatine but also 3g sugar, you'll need 5 gummies 
                daily = 15g sugar per day just from your supplement!
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Educational Content */}
        <div className="space-y-8 mb-12">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">🎯 The Science</CardTitle>
            </CardHeader>
            <CardContent className="prose max-w-none">
              <p className="text-gray-700 leading-relaxed">
                Creatine monohydrate powder is the most researched form of creatine supplement, 
                with decades of scientific evidence supporting its effectiveness. The recommended 
                daily dose is 5g of pure creatine monohydrate.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                Gummies contain the same compound, but you're paying a premium for:
              </p>
              <ul className="list-disc list-inside space-y-2 mt-2 text-gray-700">
                <li>Added sugars (15-17.5g per day)</li>
                <li>Artificial flavors and colors</li>
                <li>Gelatin and binding agents</li>
                <li>Fancy packaging and marketing</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">💰 The Cost Reality</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed">
                A 60-count pack of creatine gummies costs £20-25 and lasts just 8-12 days depending 
                on the brand. That's <strong className="text-red-600">£57-75 per month</strong>.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                A 1kg tub of pure creatine monohydrate powder costs around £35 and lasts 
                approximately 6-7 months. That's <strong className="text-green-600">£5.77 per month</strong>.
              </p>
              <div className="bg-green-50 border-l-4 border-green-500 p-4 mt-6">
                <p className="font-bold text-green-800 text-lg">
                  Annual Savings: £600-750 by switching to powder
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">🍬 The Sugar Problem</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed">
                To get the recommended 5g of creatine daily from gummies, you'll consume:
              </p>
              <ul className="list-disc list-inside space-y-2 mt-4 text-gray-700">
                <li><strong>MyProtein:</strong> 15g sugar per day = 5.5kg per year</li>
                <li><strong>Applied Nutrition:</strong> 17.5g sugar per day = 6.4kg per year</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                That's equivalent to eating 5-7 chocolate bars per week just from your 
                creatine supplement!
              </p>
            </CardContent>
          </Card>
        </div>

        {/* The Solution */}
        <Card className="bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-2xl">
          <CardHeader>
            <CardTitle className="text-3xl text-center">✅ The Solution</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-6">
            <p className="text-xl leading-relaxed">
              Get the <strong>most cost-effective and pure</strong> creatine supplement:
            </p>
            <div className="bg-white/10 rounded-lg p-6">
              <h3 className="text-2xl font-bold mb-2">Starter Pack Recommendation</h3>
              <p className="text-lg">Pure Creatine Monohydrate Powder</p>
              <p className="text-sm opacity-90 mt-2">+ Optional: Flavor drops or mix with your protein shake</p>
            </div>
            <Button 
              size="lg" 
              className="bg-white text-green-600 hover:bg-gray-100 font-bold text-lg px-8 py-6"
              asChild
              data-testid="starter-pack-button"
            >
              <a 
                href="https://www.myprotein.com/sports-nutrition/creatine-monohydrate/10530421.html" 
                target="_blank" 
                rel="nofollow sponsored noopener noreferrer"
              >
                Get the Most Cost-Effective Starter Pack (Powder) →
              </a>
            </Button>
            <p className="text-sm opacity-75">
              Start saving today. Your wallet and health will thank you.
            </p>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center space-y-4">
            <p className="text-sm text-gray-900">
              <strong>Gummy Truth UK © 2024</strong>
            </p>
            <div className="flex justify-center space-x-6 text-sm">
              <Link to="/privacy" className="text-blue-600 hover:underline">
                Privacy Policy
              </Link>
              <Link to="/" className="text-blue-600 hover:underline">
                Calculator
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function PrivacyPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center space-x-2">
              <div className="text-2xl font-bold text-green-600">🍬</div>
              <h1 className="text-2xl font-bold text-gray-900">Gummy Truth UK</h1>
            </Link>
            <Link to="/" className="text-sm font-medium text-gray-700 hover:text-gray-900">
              ← Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
        
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-6">
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">Information We Collect</h2>
            <p className="text-gray-700 leading-relaxed">
              Gummy Truth UK is a comparative cost calculator tool. We do not collect, store, or process 
              any personal information from our users. All calculations are performed locally in your browser.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">Cookies</h2>
            <p className="text-gray-700 leading-relaxed">
              This website may use basic cookies for analytics purposes to help us understand how visitors 
              use the site. These cookies do not contain any personally identifiable information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">Third-Party Links</h2>
            <p className="text-gray-700 leading-relaxed">
              Our website contains affiliate links to third-party retailers (Amazon, MyProtein, etc.). 
              When you click these links, you will be subject to the privacy policies of those websites. 
              We may earn a small commission from purchases made through these links at no extra cost to you.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">Data Security</h2>
            <p className="text-gray-700 leading-relaxed">
              Since we don't collect personal data, there is no user data to secure. All calculator 
              operations happen in your browser and are not transmitted to our servers.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">Changes to This Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this privacy policy from time to time. Any changes will be posted on this page 
              with an updated revision date.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">Contact</h2>
            <p className="text-gray-700 leading-relaxed">
              If you have any questions about this privacy policy, please contact us at:{' '}
              <a href="mailto:Gummytruth.uk@gmail.com" className="text-blue-600 hover:underline font-medium">
                Gummytruth.uk@gmail.com
              </a>
            </p>
          </section>

          <div className="border-t pt-6 mt-8">
            <p className="text-sm text-gray-500">
              Last Updated: January 2024
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center space-y-4">
            <p className="text-sm text-gray-900">
              <strong>Gummy Truth UK © 2024</strong>
            </p>
            <div className="flex justify-center space-x-6 text-sm">
              <Link to="/" className="text-blue-600 hover:underline">
                Calculator
              </Link>
              <Link to="/guide" className="text-blue-600 hover:underline">
                Creatine Guide
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

import BlogIndex from './pages/BlogIndex';
import BlogArticle from './pages/BlogArticle';
import MethodologyPage from './pages/MethodologyPage';

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/guide" element={<GuidePage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/creatine-gummies-vs-powder-cost" element={<BlogArticle />} />
        <Route path="/methodology" element={<MethodologyPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;